import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  directoryApi, type AddProjectContactInput, type CompanyInput, type PersonInput, type ProjectContactEntry, type ProjectDirectory,
  type DirectoryTeamMember,
} from '@/lib/apiClient';
import { Button, Modal } from '@/components';
import { DirectoryStyles } from './directoryStyles';
import { BookCompanyPicker, CompanyFields, PersonFields, emptyCompany, emptyPerson } from './directoryFields';

/** Roles whose people the job's Intacct subcontractors are offered for first. */
const TRADE_ROLES = new Set(['subcontractor', 'supplier']);

/**
 * Where a new person works: a company already in the book (picked, or still being
 * searched for), a new one, or none.
 */
type CompanyChoice =
  | { kind: 'book'; picked: { id: number; name: string } | null }
  | { kind: 'new'; value: CompanyInput }
  | { kind: 'none' };

/**
 * Put someone on the job (#598): pick their role here, then someone already in
 * the address book — the job's Intacct subcontractors first, then a search — or
 * someone new, who goes into the book too. Duplicates are refused by the API,
 * which names the entry to pick instead; the message is shown as it comes.
 */
export default function AddContactDialog({ projectId, directory, onClose, onAdded }: {
  projectId: number;
  directory: ProjectDirectory;
  onClose: () => void;
  onAdded: (d: { team: DirectoryTeamMember[]; contacts: ProjectContactEntry[] }) => void;
}) {
  const [role, setRole] = useState('subcontractor');
  const [note, setNote] = useState('');
  const [mode, setMode] = useState<'book' | 'new'>('book');
  const [picked, setPicked] = useState<{ id: number; label: string } | null>(null);
  const [person, setPerson] = useState<PersonInput>(emptyPerson);
  const [company, setCompany] = useState<CompanyChoice>({ kind: 'none' });
  // A new company's details, kept while another choice is looked at: a check of
  // the book that it isn't there already shouldn't throw away what was typed (review).
  const [newDraft, setNewDraft] = useState<CompanyInput | null>(null);
  const choose = (next: CompanyChoice) => {
    if (company.kind === 'new') setNewDraft(company.value);
    setCompany(next);
  };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  // The search, a quarter-second after typing stops.
  const [typed, setTyped] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setTerm(typed.trim()), 250);
    return () => window.clearTimeout(t);
  }, [typed]);
  const searchQ = useQuery({ queryKey: ['directory-search', term], queryFn: () => directoryApi.search(term), enabled: term.length >= 2 });
  const subsQ = useQuery({ queryKey: ['directory-suggestions', projectId], queryFn: () => directoryApi.suggestions(projectId), staleTime: 60_000 });

  /** Someone new at this company: from the book if it's there, else a new one with its name filled in. */
  const newAt = (c: { id: number; name: string } | { name: string }) => {
    setMode('new');
    setPicked(null);
    setCompany('id' in c ? { kind: 'book', picked: { id: c.id, name: c.name } } : { kind: 'new', value: emptyCompany(c.name, role === 'supplier' ? 'supplier' : 'subcontractor') });
  };

  const ready = mode === 'book'
    ? !!picked
    : !!person.name.trim()
      && (company.kind !== 'new' || !!company.value.name.trim())
      && (company.kind !== 'book' || !!company.picked);

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    const base = { role, note: note.trim() || null };
    const body: AddProjectContactInput = mode === 'book'
      ? { ...base, contactId: picked!.id }
      : {
        ...base,
        person,
        ...(company.kind === 'book' ? { companyId: company.picked!.id } : company.kind === 'new' ? { company: company.value } : {}),
      };
    try {
      const { team, contacts } = await directoryApi.add(projectId, body);
      // The book may have a new company or person now: the job's Intacct subs and
      // any search kept from before would still call them "not in the address
      // book yet", and adding them again from there is refused as a duplicate (review).
      void qc.invalidateQueries({ queryKey: ['directory-suggestions'] });
      void qc.invalidateQueries({ queryKey: ['directory-search'] });
      onAdded({ team, contacts });
    } catch (e) {
      setError((e as Error).message || 'They weren’t added. Try again.');
      // Refused because the book moved on (a company just added, say): what was
      // offered may be stale, so ask again rather than offer it once more (review).
      void qc.invalidateQueries({ queryKey: ['directory-suggestions'] });
      void qc.invalidateQueries({ queryKey: ['directory-search'] });
      // …and the job's list too, as Edit and Remove do: refused as "already on
      // this job", they should be there to see behind the message (review).
      void qc.invalidateQueries({ queryKey: ['project-contacts', projectId] });
    } finally {
      setBusy(false);
    }
  };

  const subs = TRADE_ROLES.has(role) ? subsQ.data?.subs ?? [] : [];

  return (
    <Modal isOpen onClose={onClose} title="Add a contact to this job" maxWidth={720}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => { void submit(); }} isLoading={busy} disabled={!ready}>Add to Job</Button>
        </>
      }>
      <DirectoryStyles />
      <div className="dir-form">
        <div className="dir-grid2">
          <label className="dir-field"><span>Role on this job</span>
            <select value={role} onChange={(e) => setRole(e.target.value)} disabled={busy}>
              {directory.roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </label>
          <label className="dir-field"><span>Note for this job</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} disabled={busy} placeholder="Optional — e.g. site contact for the roof" />
          </label>
        </div>

        <div className="dir-tabs" role="group" aria-label="Who">
          <button type="button" className="dir-tab" aria-pressed={mode === 'book'} onClick={() => setMode('book')}>From the address book</button>
          <button type="button" className="dir-tab" aria-pressed={mode === 'new'} onClick={() => { setMode('new'); setPicked(null); }}>Someone new</button>
        </div>

        {mode === 'book' && (
          picked ? (
            <div className="dir-chosen">
              <span>{picked.label}</span>
              <button type="button" className="dir-act" onClick={() => setPicked(null)}>Change</button>
            </div>
          ) : (
            <>
              {subs.length > 0 && (
                <>
                  <p className="dir-legend">This job’s subcontractors (from Intacct)</p>
                  <div className="dir-pick">
                    {subs.map((s) => (s.company && s.company.people.length > 0 ? (
                      s.company.people.map((p) => (
                        <button key={`${s.name}:${p.id}`} type="button" className="dir-pick-row" onClick={() => setPicked({ id: p.id, label: `${p.name} · ${s.company!.name}` })}>
                          <span><strong>{p.name}</strong>{p.title ? ` · ${p.title}` : ''} <span className="dir-muted">· {s.company!.name}</span></span>
                          <span className="dir-muted">{p.email ?? ''}</span>
                        </button>
                      )).concat(
                        <button key={`${s.name}:new`} type="button" className="dir-pick-row" onClick={() => newAt({ id: s.company!.id, name: s.company!.name })}>
                          <span>＋ Someone new at {s.company.name}</span>
                        </button>,
                      )
                    ) : (
                      <button key={s.name} type="button" className="dir-pick-row" onClick={() => newAt(s.company ? { id: s.company.id, name: s.company.name } : { name: s.name })}>
                        <span>＋ Add someone at {s.name}</span>
                        <span className="dir-muted">{s.company ? 'in the address book, nobody yet' : 'not in the address book yet'}</span>
                      </button>
                    )))}
                  </div>
                </>
              )}
              <label className="dir-field"><span>Search the address book</span>
                <input type="search" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Name, email or company" autoFocus />
              </label>
              {term.length >= 2 && (
                searchQ.isPending ? <p className="dir-muted dir-small">Searching…</p>
                  : searchQ.isError ? <div className="dir-error">Couldn’t search the address book. {(searchQ.error as Error).message}</div>
                    : (searchQ.data.people.length + searchQ.data.companies.length === 0) ? (
                      <p className="dir-muted dir-small">Nobody in the address book matches “{term}”. Add them as someone new.</p>
                    ) : (
                      <div className="dir-pick">
                        {searchQ.data.people.map((p) => (
                          <button key={`p${p.id}`} type="button" className="dir-pick-row" onClick={() => setPicked({ id: p.id, label: `${p.name}${p.company ? ` · ${p.company.name}` : ''}` })}>
                            <span><strong>{p.name}</strong>{p.title ? ` · ${p.title}` : ''}{p.company ? <span className="dir-muted"> · {p.company.name}</span> : null}</span>
                            <span className="dir-muted">{p.email ?? ''}</span>
                          </button>
                        ))}
                        {searchQ.data.companies.map((c) => (
                          <button key={`c${c.id}`} type="button" className="dir-pick-row" onClick={() => newAt({ id: c.id, name: c.name })}>
                            <span>＋ Someone new at <strong>{c.name}</strong></span>
                            <span className="dir-muted">{directory.kinds.find((k) => k.key === c.kind)?.label ?? c.kind}</span>
                          </button>
                        ))}
                      </div>
                    )
              )}
            </>
          )
        )}

        {mode === 'new' && (
          <>
            <PersonFields value={person} onChange={setPerson} disabled={busy} />
            <p className="dir-legend">Company</p>
            <div className="dir-tabs" role="group" aria-label="Their company">
              <button type="button" className="dir-tab" aria-pressed={company.kind === 'book'}
                onClick={() => choose({ kind: 'book', picked: company.kind === 'book' ? company.picked : null })}>
                One in the address book
              </button>
              <button type="button" className="dir-tab" aria-pressed={company.kind === 'new'}
                onClick={() => setCompany({ kind: 'new', value: company.kind === 'new' ? company.value : newDraft ?? emptyCompany('', role === 'supplier' ? 'supplier' : 'subcontractor') })}>
                A new company
              </button>
              <button type="button" className="dir-tab" aria-pressed={company.kind === 'none'} onClick={() => choose({ kind: 'none' })}>No company</button>
            </div>
            {company.kind === 'book' && (
              company.picked ? (
                <div className="dir-chosen">
                  <span>At <strong>{company.picked.name}</strong>, already in the address book</span>
                  <button type="button" className="dir-act" onClick={() => setCompany({ kind: 'book', picked: null })}>Change</button>
                </div>
              ) : (
                <BookCompanyPicker directory={directory} onPick={(c) => setCompany({ kind: 'book', picked: c })} />
              )
            )}
            {company.kind === 'new' && (
              <CompanyFields value={company.value} onChange={(value) => setCompany({ kind: 'new', value })} directory={directory} disabled={busy} />
            )}
          </>
        )}

        {error && <div className="dir-error" role="alert">{error}</div>}
      </div>
    </Modal>
  );
}
